import React, { useMemo, useState } from 'react';
import { KpiCard, Widget, Card } from '../../components/ui';
import { useChurchConfig } from '../../hooks';
import { useTheme } from '../../theme/ThemeContext';
import { getTokens } from '../../theme/tokens';
import { formatCurrency, formatNumber } from '../../utils/format';
import { MODULE_ACCENTS, APP_TITLE } from '../../utils/constants';
import churchLogo from '../../church.png';
import useDashboardData from './useDashboardData';

/** The pill that clears the filter. Not a real category. */
const ALL = 'All';

/** A friendly greeting for the time of day. */
const greetingFor = (hour) =>
  (hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening');

const DashboardHome = ({ modules = [], lastActiveModule, onSelectModule, hasAccess, userName }) => {
  const { isDarkMode } = useTheme();
  const t = getTokens(isDarkMode);
  const data = useDashboardData(true);
  /** The day the summary was taken — printed under the masthead. */
  const printReference = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const config = useChurchConfig();

  const [activeCategory, setActiveCategory] = useState(ALL);

  // The parish's own uploaded logo and name brand the header; the bundled mark
  // stands in until Settings has one, so the banner is never left blank.
  const logoSrc = config.logoBase64 || churchLogo;
  const churchName = config.churchName || APP_TITLE;
  const brand = config.themeColor || t.primary;
  const now = new Date();
  const greeting = greetingFor(now.getHours());
  const firstName = String(userName || '').trim().split(/\s+/)[0];
  const contactLine = [config.address, config.contactNo].filter(Boolean).join('  •  ');

  // Only offer a category the user can actually reach something in, so a pill
  // can never lead to an empty grid. The tiles already arrive category by
  // category in the parish's chosen order, so the pills follow that order.
  const categories = useMemo(() => {
    const present = [];
    modules.forEach((m) => { if (!present.includes(m.category)) present.push(m.category); });
    return [ALL, ...present];
  }, [modules]);

  const displayedModules = useMemo(
    () => (activeCategory === ALL
      ? modules
      : modules.filter((m) => m.category === activeCategory)),
    [modules, activeCategory]
  );

  const kpis = [
    { label: 'Registered Families',     value: data.familyCount,  icon: '👨‍👩‍👧‍👦', color: '#1976D2', bgLight: 'rgba(187, 222, 251, 0.95)', bgDark: '#131b26' },
    { label: 'Total Members',           value: data.memberCount,  icon: '👥',   color: '#388E3C', bgLight: 'rgba(200, 230, 201, 0.95)', bgDark: '#152117' },
    // Guests sit apart from the roll; the label carries how many stays end soon.
    { label: data.guestsEndingSoon ? `Guests · ${data.guestsEndingSoon} ending soon` : 'Guests',
      value: data.guestCount, icon: '🧳', color: '#6D4C41', bgLight: 'rgba(215, 204, 200, 0.95)', bgDark: '#262220' },
    { label: 'Sunday School Students',  value: data.studentCount, icon: '🏫',   color: '#F57C00', bgLight: 'rgba(255, 224, 178, 0.95)', bgDark: '#261a12' }
  ];

  const utilizationColor =
    data.utilization > 90 ? t.danger : data.utilization > 75 ? t.warning : t.income;

  const rowDivider = (isLast) => ({
    borderBottom: isLast ? 'none' : (isDarkMode ? '1px solid #333' : '1px solid rgba(0,0,0,0.05)')
  });

  // Each widget ends with a jump to the module it summarises. The widget is
  // already access-gated on that same module, so the link is always reachable.
  const widgetLink = (label, moduleId) => (
    <button
      type="button"
      onClick={() => onSelectModule(moduleId)}
      style={{
        width: '100%', marginTop: 'auto', padding: '9px',
        backgroundColor: t.bgSubtle,
        border: `1px solid ${t.border}`, borderRadius: t.radius.md,
        color: t.textAccent,
        fontWeight: t.fontWeight.bold, fontSize: t.fontSize.base, cursor: 'pointer'
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      {/* Prints as a one-page parish summary: figures and today's activity,
          without the module tiles, which are navigation rather than content. */}
      <style>{`
        .pulse-sync { animation: pulse 1.5s infinite; }
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
      `}</style>

      {/* --- Branded hero header --- */}
      <div
       
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap',
          gap: t.spacing.lg, padding: '22px 26px', marginBottom: t.spacing.xl,
          borderRadius: t.radius.xl, color: '#FFFFFF',
          // Solid brand colour with a soft top-left sheen and a darker
          // bottom-right, so the banner reads on any accent the parish picks.
          background: `linear-gradient(135deg, rgba(255,255,255,0.14), rgba(0,0,0,0.20)), ${brand}`,
          boxShadow: '0 10px 26px rgba(0,0,0,0.18)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px', minWidth: 0 }}>
          <div style={{
            width: '70px', height: '70px', borderRadius: '18px', backgroundColor: '#FFFFFF',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            padding: '8px', boxSizing: 'border-box', boxShadow: '0 6px 16px rgba(0,0,0,0.22)'
          }}>
            <img
              src={logoSrc} alt={churchName} className="notranslate" translate="no"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold, opacity: 0.92 }}>
              {greeting}{firstName ? `, ${firstName}` : ''} 👋
            </div>
            <h1 style={{
              margin: '2px 0 0', fontSize: '26px', fontWeight: t.fontWeight.black, letterSpacing: '0.3px',
              color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '520px'
            }}>
              {churchName}
            </h1>
            {contactLine && (
              <div style={{ fontSize: t.fontSize.sm, opacity: 0.85, marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '520px' }}>
                {contactLine}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
          <span style={{ fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold, opacity: 0.95 }}>
            {now.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {data.isSyncing && (
              <span className="pulse-sync" style={{
                fontSize: t.fontSize.sm, color: '#FFFFFF', fontWeight: t.fontWeight.bold,
                backgroundColor: 'rgba(255,255,255,0.22)', padding: '5px 12px', borderRadius: t.radius.pill
              }}>
                ↻ Live Syncing…
              </span>
            )}
          </div>
        </div>
      </div>

      {/* On paper, a plain heading sits under the print letterhead. */}
      <h2 style={{ color: t.textMain, marginTop: 0 }}>System Overview</h2>

      {/* --- KPI tiles --- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: t.spacing.lg, marginBottom: t.spacing.xxl }}>
        {kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={formatNumber(kpi.value)}
            icon={kpi.icon}
            color={kpi.color}
          />
        ))}
      </div>

      {/* --- Widgets --- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: t.spacing.xl, marginBottom: '40px', padding: '5px' }}>

        {hasAccess('Church Calendar') && (
          <Widget title="🗓️ Today's Activities" accent="purple">
            {data.todayEvents.map((ev, idx) => (
              <div key={idx} style={{
                display: 'flex', alignItems: 'center', gap: t.spacing.md,
                marginBottom: '12px', paddingBottom: '12px',
                ...rowDivider(idx === data.todayEvents.length - 1)
              }}>
                <div style={{ fontWeight: t.fontWeight.bold, color: t.info, minWidth: '50px', fontSize: t.fontSize.md }}>{ev.time}</div>
                <div style={{ color: t.textMain, fontWeight: t.fontWeight.bold, fontSize: t.fontSize.md, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</div>
              </div>
            ))}
            {widgetLink('View Full Calendar →', 'Church Calendar')}
          </Widget>
        )}

        {hasAccess('Budget Management') && (
          <Widget title="📈 Financial Snapshot (This Month)" accent="mint">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: t.spacing.md }}>
              <span style={{ color: t.textMuted, fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold }}>Total Income</span>
              <span style={{ color: t.income, fontSize: t.fontSize.lg, fontWeight: t.fontWeight.black }}>{formatCurrency(data.mIncome, '₹0')}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: t.spacing.lg }}>
              <span style={{ color: t.textMuted, fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold }}>Total Expenses</span>
              <span style={{ color: t.expense, fontSize: t.fontSize.lg, fontWeight: t.fontWeight.black }}>{formatCurrency(data.mExpense, '₹0')}</span>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: t.fontSize.sm, marginBottom: t.spacing.xs, fontWeight: t.fontWeight.bold, color: t.textMuted }}>
                <span>Annual Budget Utilization</span>
                <span>{data.utilization}%</span>
              </div>
              <div style={{ width: '100%', backgroundColor: isDarkMode ? '#444' : 'rgba(0,0,0,0.1)', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                <div style={{ width: `${data.utilization}%`, backgroundColor: utilizationColor, height: '100%', transition: 'width 0.4s ease' }} />
              </div>
            </div>
            <div style={{ marginTop: t.spacing.lg }}>
              {widgetLink('Manage Finances →', 'Budget Management')}
            </div>
          </Widget>
        )}

        {hasAccess('Birthday & Anniversary') && (
          <Widget title="🎂 Birthdays & Anniversaries" accent="peach">
            {data.upcomingBirthdays.map((b, idx) => (
              <div key={idx} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '12px', paddingBottom: '12px',
                ...rowDivider(idx === data.upcomingBirthdays.length - 1)
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', overflow: 'hidden' }}>
                  <span style={{ color: t.textMain, fontWeight: t.fontWeight.bold, fontSize: t.fontSize.md, whiteSpace: 'nowrap' }}>{b.name}</span>
                  {b.desc && <span style={{ color: t.textMuted, fontSize: t.fontSize.sm, whiteSpace: 'nowrap' }}>- {b.date}, {b.desc}</span>}
                </div>
              </div>
            ))}
            {widgetLink('View Full List →', 'Birthday & Anniversary')}
          </Widget>
        )}
      </div>

      <hr style={{ border: 'none', borderTop: `1px solid ${t.border}`, marginBottom: t.spacing.xxl }} />

      {/* --- Module grid, filtered by category --- */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: t.spacing.lg, flexWrap: 'wrap', gap: t.spacing.md
      }}>
        <h2 style={{ color: t.textMain, margin: 0, fontSize: t.fontSize.xl, fontWeight: t.fontWeight.black }}>Modules</h2>

        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '5px' }}>
          {categories.map((cat) => {
            const selected = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                aria-pressed={selected}
                style={{
                  padding: '6px 14px', minHeight: '34px', borderRadius: t.radius.pill,
                  fontWeight: selected ? t.fontWeight.bold : t.fontWeight.medium, fontSize: t.fontSize.base, cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  border: `1px solid ${selected ? t.primary : t.borderStrong}`,
                  backgroundColor: selected ? t.primary : t.bgMain,
                  color: selected ? t.textOnPrimary : t.textMuted
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: t.spacing.lg, width: '100%', padding: '5px' }}>
        {displayedModules.map((mod) => (
          <Card
            key={mod.id}
            icon={mod.icon}
            title={mod.title}
            description={mod.desc}
            highlighted={mod.id === lastActiveModule}
            accent={MODULE_ACCENTS[mod.id]}
            onClick={() => onSelectModule(mod.id)}
          />
        ))}
      </div>

      {displayedModules.length === 0 && (
        <div style={{
          padding: t.spacing.xxl, textAlign: 'center', color: t.textFaint,
          backgroundColor: t.bgMain, borderRadius: t.radius.xl,
          border: `1px dashed ${t.border}`
        }}>
          No modules found in the &quot;<strong>{activeCategory}</strong>&quot; category.
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
